import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Alert,
} from "@mui/material";
import { LocalHospital } from "@mui/icons-material";

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username && password) {
      onLogin();
    } else {
      setError("Please enter both username and password");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 50%, #E91E63 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>\')',
          opacity: 0.1,
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            animation: "fadeInUp 0.8s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(30px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/Logo.jpeg`}
            alt="Elaj Group Logo"
            sx={{
              height: 100,
              width: "auto",
              m: 2,
              objectFit: "contain",
              borderRadius: 2,
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              p: 1,
            }}
          />
          <Typography
            component="h1"
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "white",
              textAlign: "center",
              mb: 1,
              textShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
            }}
          >
            Elaj Group ERP
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              textAlign: "center",
              mb: 4,
              fontWeight: 400,
              textShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
            }}
          >
            Healthcare Enterprise Resource Planning System
          </Typography>
          <Card
            sx={{
              width: "100%",
              maxWidth: 400,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: 3,
              boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.2)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box component="form" onSubmit={handleSubmit}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      "& .MuiAlert-icon": {
                        color: "error.main",
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    mb: 3,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
                    boxShadow: "0px 4px 12px rgba(46, 125, 138, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1A5A66 0%, #2E7D8A 100%)",
                      boxShadow: "0px 6px 16px rgba(46, 125, 138, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Sign In
                </Button>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    background: "rgba(46, 125, 138, 0.05)",
                    borderRadius: 2,
                    border: "1px solid rgba(46, 125, 138, 0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Demo Mode - Use any credentials to login
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
